import { useTranslation } from 'react-i18next';

import { Switch } from '@/components/ui/switch';
import { useCapabilities } from '@/hooks/use-capabilities';
import {
  CAPABILITIES, blockingDependents, unmetPrerequisites,
  type CapabilityDef, type CapabilityId,
} from '@/lib/capabilities';

/**
 * Where a capability is turned on.
 *
 * ## One shape for two relationships
 *
 * An indent means "this widens the thing above it", and it covers both kinds of widening:
 *
 *   Question Versions          a capability
 *     Linked Scenarios         a DIFFERENT capability that rides on it
 *   Variables                  a capability, at its narrowest rung
 *     Number blocks            the same capability, one rung wider
 *       Everywhere             wider again
 *
 * Both are read off the registry — `requires` for the first, `rungs` for the second — so a
 * capability that gains a prerequisite or a rung moves on its own.
 *
 * ## A ladder is monotone
 *
 * Each rung switch writes a LEVEL, not a boolean: turning rung 3 on sets the level to 3, which is
 * why the rungs above it light up with it, and turning rung 2 off drops to 1, which is why rung 3
 * goes dark. A ladder whose middle rung could be off while its top was on would not be a level at
 * all, and `levelOf` is what every gate reads.
 *
 * ## Step-wise, not carry-through
 *
 * Turning something on does NOT silently raise its prerequisites. Instead a switch is live only
 * at the frontier: you can turn it on when the thing directly above it is already on, and off
 * when nothing below it is. So reaching Answer Recall's top rung is four deliberate presses, and
 * each one is an acknowledgement that the step below it is being turned on too.
 *
 * The alternative — raise the prerequisites and say so afterwards — reaches the same state with
 * one press and no acknowledgement, which is the opposite of what this page is for.
 *
 * NO REASON TEXT on a disabled switch, because the structure already is the reason: the only
 * thing holding a switch is the row immediately above it, which is on screen and one indent out.
 *
 * ## Why `Switch` rather than what this page already uses
 *
 * The Settings page predates the shared library: its one toggle is a bare
 * `<input type='checkbox'>` and its theme picker hand-rolls a `role='radiogroup'` that
 * `SegmentedControl` already provides. `feedback_agavi_components_before_browser_defaults` says to
 * reach for `components/ui` first, so this does. `CheckboxRow` is specifically wrong here — it
 * forces a disabled toggle to READ AS OFF, which is right for a setting a grading mode took away
 * and a lie about a capability something above it is holding on.
 */
export default function CapabilitiesSection() {
  const roots = CAPABILITIES.filter((c) => !c.requires?.length);
  return (
    /*
     * A RULE BETWEEN SETS, air inside them.
     *
     * Eleven switches in one column read as a wall: every gap was the same size, so nothing said
     * where one capability ended and the next began. The horizontal rule separates the sets and
     * the vertical rule inside a set says depth — two line treatments doing two different jobs,
     * which is what lets the rows themselves stay tight.
     */
    <div className='divide-y divide-ws-border'>
      {roots.map((def) => (
        <div key={def.id} className='py-5 first:pt-0 last:pb-0'>
          <CapabilityTree def={def} />
        </div>
      ))}
    </div>
  );
}

/** ONE call site, so a row's heading and anything else naming it cannot drift apart. */
const nameOf = (t: (key: string) => string, id: CapabilityId) => t(`profile.capabilities.${id}.name`);

const INDENT = 'ml-2 mt-3 space-y-3 border-l border-ws-border pl-4';

function CapabilityTree({ def }: { def: CapabilityDef }) {
  const { t } = useTranslation();
  const dependents = CAPABILITIES.filter((c) => c.requires?.[0]?.id === def.id);
  /* Named in the DOM, the way `data-role` and `data-field-id` already are elsewhere: the nesting
     IS the design here, and a test that walks parentElement re-breaks every time a row gains a
     line. */
  return (
    <div data-capability={def.id} className='space-y-3'>
      <RungSwitch def={def} rung={1}
        label={nameOf(t, def.id)}
        note={t(`profile.capabilities.${def.id}.description`)} />
      <Ladder def={def} rung={2} />
      {dependents.length > 0 && (
        <div className={INDENT}>
          {dependents.map((child) => <CapabilityTree key={child.id} def={child} />)}
        </div>
      )}
    </div>
  );
}

/** Rungs 2 and up, each one indent further than the last. Rung 1 is the capability's own row. */
function Ladder({ def, rung }: { def: CapabilityDef; rung: number }) {
  const { t } = useTranslation();
  if (rung > def.rungs.length) return null;
  return (
    <div className={INDENT}>
      <RungSwitch def={def} rung={rung}
        label={t(`profile.capabilities.${def.id}.rungs.${def.rungs[rung - 1]}`)}
        note={t(`profile.capabilities.${def.id}.rungNotes.${def.rungs[rung - 1]}`)} />
      <Ladder def={def} rung={rung + 1} />
    </div>
  );
}

function RungSwitch(
  { def, rung, label, note }: { def: CapabilityDef; rung: number; label: string; note: string },
) {
  const { t } = useTranslation();
  const { state, level, setLevel } = useCapabilities();
  const at = level(def.id);
  const on = at >= rung;
  /*
   * THE FRONTIER, and nothing else, moves.
   *
   * On is available only from the rung directly below this one, and only once this capability's
   * own prerequisites are met. Off is available only from this exact rung, and only once nothing
   * depends on what it would drop to. Both questions are already answered by the graph.
   */
  const canTurnOn = at === rung - 1 && unmetPrerequisites(state, def.id, rung).length === 0;
  const canTurnOff = at === rung && blockingDependents(state, def.id, rung - 1).length === 0;
  const held = !canTurnOn && !canTurnOff;
  /*
   * THE TITLE CARRIES THE STATE, so the frontier is readable without tracing eleven switches.
   *
   * Three states, and they are not three shades of the same thing: tinted is ON, plain is off and
   * reachable, dim is off and not reachable yet. A row that is ON but held — Question Versions
   * under a live Linked Scenarios — stays tinted, because it IS on and dimming it would say
   * otherwise (the same trap `CheckboxRow`'s forced-off read exists to avoid).
   *
   * Colour is never the only marker: an active row is heavier as well as tinted, the switch
   * beside it already says on or off, and a held switch is already inert. The tone makes the
   * page scannable, it does not carry the meaning.
   */
  const titleTone = on ? 'font-semibold text-ws-primary'
    : held ? 'font-medium text-ws-muted'
      : 'font-medium text-ws-text';
  /*
   * A RUNG'S ACCESSIBLE NAME CARRIES ITS CAPABILITY.
   *
   * Visually a rung sits under the capability that owns it and "Number blocks" is unambiguous.
   * Read aloud it is not: both ladders have a rung called "Numerical only", and a listener moving
   * switch to switch gets no indentation. The visible label stays short; the name says which.
   */
  return (
    <div data-rung={`${def.id}:${rung}`}>
      <div className='flex items-center justify-between gap-4'>
        <p className={`text-base ${titleTone}`}>{label}</p>
        {/* On goes to THIS rung, off drops to the one below it — see "a ladder is monotone". */}
        <Switch
          on={on}
          onChange={(next) => void setLevel(def.id, next ? rung : rung - 1)}
          label={rung === 1 ? label : `${nameOf(t, def.id)}: ${label}`}
          disabled={held}
          className='shrink-0'
        />
      </div>
      {/*
       * The note is NOT an `aria-describedby` on the switch.
       *
       * A listener stepping through switches would hear the whole paragraph on every one, and a
       * teacher reading the page gets it from the layout anyway. It is page CONTENT — the one
       * screen whose job is explaining what a capability is — rather than help text hung off a
       * control. `pr-14` keeps the last line clear of the switch above it.
       */}
      <p className='mt-1 pr-14 text-xs italic leading-relaxed text-ws-muted'>{note}</p>
    </div>
  );
}
